import * as api from 'api'
import constants from 'config/constants'
import { getHistory } from 'libs/history'
import * as R from 'ramda'
import auth from '../../auth'
import * as syncActions from '../sync'

export * from './admin'
export * from './customer'
export * from './profile'
export * from './user'

export const getRaterData = () => (dispatch) =>
  api
    .getRaterData({ all: 'true' })
    .then((res) => res.json())
    .catch(() => {
      console.error('Failed while getting rater data.')
    })
    .then((data) => {
      dispatch(syncActions.setRater(data))
    })

export const getCustomerRater = (email) => (dispatch) =>
  api
    .getRaterData({
      all: 'true',
      client_email: email,
    })
    .then((res) => res.json())
    .catch(() => {
      console.error('Failed while getting customer rater.')
    })
    .then((data) => {
      dispatch(syncActions.setCustomerRater(email, data))
    })

export const getCustomerNPS = (email) => (dispatch) =>
  api
    .getAnswersData({
      all: 'true',
      client_email: email,
      metric: 'NPS',
    })
    .then((res) => res.json())
    .catch(() => {
      console.error('Failed while getting customer NPS.')
    })
    .then((data) => {
      dispatch(syncActions.setCustomerNPS(email, data.results))
    })

export const getNpsData = () => (dispatch) =>
  api
    .getNpsData({ all: 'true' })
    .then((res) => res.json())
    .catch(() => console.error('Failed while GETing NPS data'))
    .then((nps) => dispatch(syncActions.setNps(nps)))

export const getPaginatedFeedback = (limit, offset) => (dispatch) =>
  api
    .getFeedbackData({
      limit,
      offset,
    })
    .then((res) => res.json())
    .catch(() => {
      console.error('Failed while getting feedback data.')
    })
    .then((data) => {
      dispatch(syncActions.setPaginatedFeedback(data.count, data.results))
    })

export const getAnswers = (query) => (dispatch) =>
  api
    .getAnswersData(query)
    .then((res) => res.json())
    .catch(() => {
      console.error('Failed while getting Answers data.')
    })
    .then((data) => {
      dispatch(syncActions.setAnswers(data.count, data.results))
      return data
    })

export const getReliabilityData = () => (dispatch) =>
  api
    .getReliabilityData({ all: 'true' })
    .then((res) => res.json())
    .catch(() => console.error('Failed while GETing Reliability data'))
    .then((reliability) => dispatch(syncActions.setReliability(reliability)))

export const getCustomerData = (email) => (dispatch) =>
  api
    .getCustomersData({ email })
    .then((res) => res.json())
    .catch(() => {
      console.error('Failed while getting customer data.')
    })
    .then((data) => {
      dispatch(syncActions.setCustomer(data))
    })

export const getCustomersData = () => (dispatch) =>
  api
    .getCustomersData({ all: true })
    .then((res) => res.json())
    .catch(() => {
      console.error('Failed while getting customers data.')
    })
    .then((data) => {
      dispatch(syncActions.setCustomers(data.results))
    })

export const getCustomerFeedback = (email) => (dispatch) =>
  api
    .getFeedbackData({
      order: 'desc',
      client_email: email,
    })
    .then((res) => res.json())
    .catch(() => {
      console.error('Failed while getting customer feedback')
    })
    .then((data) => {
      dispatch(syncActions.setCustomerFeedback(email, data.results))
    })

export const getPaginatedCustomers = (limit, offset) => (dispatch) =>
  api
    .getCustomersData({ limit, offset })
    .then((res) => res.json())
    .catch(() => {
      console.error('Failed while getting paginated customers')
    })
    .then((data) => {
      dispatch(syncActions.setPaginatedCustomers(data.count, data.results))
    })

export const getSatisfactionRatings =
  (dateRange, surveyToken, integrationSource) => async (dispatch) => {
    try {
      const data = await api
        .getAnswersData({
          date_range: (dateRange || constants.movingAverageRange) * 2,
          metric: 'CSAT',
          all: true,
          include_fields: ['value', 'timestamp', 'id', 'sentiment', 'rating', 'metric'],
          survey_token: surveyToken,
          integration_source: integrationSource,
        })
        .then((res) => res.json())
      dispatch(syncActions.setAnswers(data.count, data.results))
      dispatch(syncActions.setTicketSatisfactionGraph(data.results))
      dispatch(syncActions.satisfactionRatingsLoaded())
    } catch (e) {
      console.error('Failed while getting satisfaction ratings', e)
      dispatch(syncActions.satisfactionRatingsLoadFailed())
    }
  }

export const loginUser = (username, password) => (dispatch) => {
  dispatch(syncActions.loggingIn())
  return auth.login(username, password).then((authData) => {
    authData.isAuthenticated
      ? dispatch(syncActions.loginSuccessful())
      : dispatch(syncActions.loginFailed(authData.error))
    return authData
  })
}

export const signupUser = (payload) => (dispatch) => {
  dispatch(syncActions.signingUp())
  return auth.signup(payload).then((authData) => {
    authData.isAuthenticated
      ? dispatch(syncActions.signupSuccessful())
      : dispatch(syncActions.signupFailed(authData.error))
    return authData.isAuthenticated
  })
}

export const getLeaderboard = (surveyToken, query) => (dispatch) =>
  api
    .getLeaderboard(surveyToken, query)
    .then((response) => {
      dispatch(
        syncActions.setTicketSatisfactionLeaderboard(response.data.count, response.data.results)
      )
    })
    .catch((error) => {
      console.error('[ACTION](getLeaderboard) failed to run')
      throw error
    })

export const markAsTestimonial = (answerDetailId, publishedAsTestimonial) => (dispatch) => {
  api
    .updateAnswerDetail({
      id: answerDetailId,
      published_as_testimonial: publishedAsTestimonial,
    })
    .then((res) => res.json())
    .catch(() => {
      console.error('Failed while update mark as testimonial')
    })
    .then((data) => {
      dispatch(syncActions.updateFeedbackTestimonial(answerDetailId, data.published_as_testimonial))
    })
}

export const getUserInfoFromToken = () => (dispatch) => {
  const userInfo = auth.getUserInfoFromToken()
  dispatch(syncActions.setUserInfo(userInfo))
}

export const updateAccountSettings = (companyId, dataBody) => async (dispatch) => {
  try {
    const response = await api.updateAccountSettings(dataBody, companyId)

    if (!response.ok) {
      // Parse error response to get detail message
      const errorData = await response.json().catch(() => ({}))
      const error = {
        message: `HTTP error! status: ${response.status}`,
        detail: errorData.detail || `HTTP error! status: ${response.status}`,
        status: response.status,
      }
      throw error
    }

    const data = await response.json()
    return dispatch(syncActions.setCompanyAccountSetting(data))
  } catch (e) {
    console.error('[ACTION] (updateAccountSettings) Failed to run', e)
    throw e
  }
}

export const removeZendeskSatisfactionSync = (companyId) => async (dispatch) => {
  const ownedBy = {
    owned_by: `http://gateway:8000/api/account/company/${companyId}/`,
  }
  const res = await api.deleteZendeskSatisfactionSync(ownedBy)
  if (res.status === 200) {
    dispatch(syncActions.removeZendeskSyncSuccess())
  } else {
    console.error('Failed while remove Zendesk satisfaction sync')
    dispatch(syncActions.removeZendeskSyncFailed())
  }
}

export const getZendeskAuthenticationSetting = () => async (dispatch) => {
  try {
    const userCompanyId = await api.getCompanyId()
    const data = await api
      .getZendeskAuthenticationData({ company_id: userCompanyId })
      .then((res) => res.json())
    dispatch(syncActions.setZendeskAuthenticationSetting(data))
    return data
  } catch (e) {
    console.error('Failed while getting ZendeskAuthenticationSetting', e)
  }
}

const getUpdateTicketTagsAndCommentSettings = (surveySettings) => {
  const hasTags = R.hasPath(['updateTicketTagsAndComment', 'tags'], surveySettings)
  const hasComment = R.hasPath(['updateTicketTagsAndComment', 'comment'], surveySettings)
  const hasUpdateTicketTagsAndCommentSetting = hasTags && hasComment
  if (hasUpdateTicketTagsAndCommentSetting) {
    return { isActive: true }
  } else {
    return {
      tags: {
        good: 'ss_good_satisfaction',
        neutral: 'ss_ok_satisfaction',
        bad: 'ss_bad_satisfaction',
      },
      comment:
        'This ticket was just rated: "[[rating]]"\n\n[[feedback_comment]]\n\nView all feedback in [[feedback_link]]',
      isActive: true,
    }
  }
}

export const getSurveySettings = (state, surveyId) =>
  R.path(['data', 'surveys', 'entity', surveyId, 'settings'], state)

export const activateZendeskIntegrationAndReturnSuccess =
  (token, email, subdomain, surveyId) => async (dispatch, getState) => {
    const isSuccess = await dispatch(authenticateZendesk(token, email, subdomain))

    if (isSuccess) {
      const surveySettings = getSurveySettings(getState(), surveyId)
      const settings = R.mergeDeepRight(surveySettings, {
        updateTicketTagsAndComment: getUpdateTicketTagsAndCommentSettings(surveySettings),
      })
      const survey = { settings }
      dispatch(updateSurveyFromId(surveyId, survey))
    }

    return isSuccess
  }

export const authenticateZendesk =
  (zendeskToken, zendeskEmail, zendeskSubdomain, isSync = false) =>
  async (dispatch) => {
    const credential = {
      company_id: auth.getUserInfoFromToken().company.id,
      zendesk_api_token: zendeskToken,
      zendesk_api_email: zendeskEmail,
      zendesk_api_subdomain: zendeskSubdomain,
      sync_zendesk_satisfaction_rating: isSync,
    }

    try {
      const data = await api.postZendeskAuthentication(credential)
      if (data.error) {
        throw new Error('Failed while authenticate zendesk')
      } else {
        dispatch(syncActions.setZendeskAuthenticationSetting(data))
        dispatch(syncActions.zendeskAuthenticationSuccess())
        return true
      }
    } catch (e) {
      dispatch(syncActions.zendeskAuthenticationFailed())
      console.error('Failed while authenticating Zendesk API credential', e)
    }

    return false
  }

export const logoutThunk = () => () => {
  auth.logout()
  getHistory().replace('/login')
}

export const createSurveyByType = (type) => async (dispatch) => {
  const surveyType = {
    type,
  }
  try {
    const data = await api.createSurvey(surveyType).then((res) => res.json())
    dispatch(syncActions.setSurvey(data))
    return data
  } catch (e) {
    console.error('Failed while creating survey and setting survey data')
  }
}

export const getSurveyList = () => async (dispatch) => {
  try {
    const data = await api.getSurveyList().then((res) => res.json())
    if (data.count === 0) {
      dispatch(syncActions.hasNoSurvey())
    }
    return dispatch(syncActions.setSurveyList(data.results))
  } catch (e) {
    console.error('Failed while fetching survey list')
    dispatch(syncActions.hasNoSurvey())
    dispatch(syncActions.hasNoSurveyAccess())
  }
}

export const transformSurveyCallToActionsToRatingTerm = (data) => {
  return R.mergeDeepRight(data, {
    settings: {
      callToAction: {
        good: {
          facebook: R.path(['callToAction', 'facebook'], data.settings),
          google:
            R.path(['callToAction', 'google'], data.settings) ||
            R.path(['callToAction', 'googlePlus'], data.settings),
          custom: R.path(['callToAction', 'custom', 'good'], data.settings),
        },
        bad: {
          custom: R.path(['callToAction', 'custom', 'bad'], data.settings),
        },
        neutral: {
          custom: R.path(['callToAction', 'custom', 'neutral'], data.settings),
        },
      },
    },
  })
}

export const getSurveyFromId = (surveyId) => async (dispatch) => {
  try {
    let getStatus
    const data = await api.getSurveyFromId(surveyId).then((res) => {
      getStatus = res.status
      return res.json()
    })

    const survey = transformSurveyCallToActionsToRatingTerm(data)
    if (getStatus !== 404) {
      dispatch(syncActions.setSurveyFromId(survey))
    }
    return survey
  } catch (e) {
    console.error('[ACTION](getSurveyFromId) Failed to run', e)
    throw e
  }
}

export const updateSurveyFromId = (surveyId, survey) => async (dispatch) => {
  try {
    const data = await api.updateSurveyFromId(surveyId, survey).then((res) => res.json())
    dispatch(syncActions.setSurveyFromId(data))
  } catch (e) {
    console.error('[ACTION](updateSurveyFromId) failed to run')
    throw e
  }
}

export const postResetPassword = (email) => async (dispatch) => {
  dispatch(syncActions.resetPassword(email))
  try {
    await api.postResetPassword({ email }).then((res) => res.json())
  } catch (e) {
    console.error('error to reset password', e)
  }
}

export const postResetPasswordConfirm = (token, password) => async (dispatch) => {
  try {
    await api.postResetPasswordConfirm({
      password,
      encoded_token: token,
    })
  } catch (e) {
    console.error('error to reset password', e)
  }
  dispatch(logoutThunk())
}

export const updateTicketTagsAndComment = (surveyId, tags, comment) => (dispatch, getState) => {
  const settings = R.mergeDeepRight(getSurveySettings(getState(), surveyId), {
    updateTicketTagsAndComment: {
      tags,
      comment,
    },
  })
  const survey = { settings }
  dispatch(updateSurveyFromId(surveyId, survey))
}

export const deactivateZendeskIntegration = (surveyId) => (dispatch, getState) => {
  const settings = R.mergeDeepRight(getSurveySettings(getState(), surveyId), {
    updateTicketTagsAndComment: {
      isActive: false,
    },
  })
  const survey = { settings }
  dispatch(updateSurveyFromId(surveyId, survey))
}

const saveZendeskEndUserCredential = async (email, password) => {
  const credential = {
    zendesk_end_user_email: email,
    zendesk_end_user_password: password,
  }

  const data = await api.postZendeskEndUserAuthentication(credential)
  if (data instanceof Error) {
    throw data
  }
}

const getAddSatisfactionSettings = (surveySettings) => {
  const hasSetOkAs = R.hasPath(['addSatisfaction', 'setOkAs'], surveySettings)
  const hasText = R.hasPath(['addSatisfaction', 'text'], surveySettings)
  const hasAddSatisfactionSetting = hasSetOkAs && hasText
  if (hasAddSatisfactionSetting) {
    return { isActive: true }
  } else {
    return {
      setOkAs: 'bad',
      text: 'This ticket received a "[[rating]]" rating. Comment: [[feedback_comment]]. Feedback provided by [[feedback_link]].',
      isActive: true,
    }
  }
}

const createNewSurveySettingsWithBackwardSyncEnabled = (state, surveyId) => {
  const surveySettings = getSurveySettings(state, surveyId)
  return {
    settings: R.mergeDeepRight(surveySettings, {
      addSatisfaction: getAddSatisfactionSettings(surveySettings),
    }),
  }
}

export const activateZendeskSatisfactionRatingBackwardSyncAndReturnSuccess =
  (email, password, surveyId) => async (dispatch, getState) => {
    try {
      await saveZendeskEndUserCredential(email, password)
      const survey = createNewSurveySettingsWithBackwardSyncEnabled(getState(), surveyId)
      dispatch(updateSurveyFromId(surveyId, survey))
      dispatch(syncActions.zendeskEndUserAuthenticationSuccess())
      return true
    } catch (error) {
      dispatch(syncActions.zendeskEndUserAuthenticationFailed())
      console.error('Failed while activating Zendesk Satisfaction Rating backward sync', error)
      return false
    }
  }

export const getZendeskEndUserCredential = () => async (dispatch) => {
  try {
    const data = await api.getZendeskEndUserCredential()
    dispatch(syncActions.setZendeskEndUserCredential(data))
  } catch (e) {
    console.error('Failed while getting Zendesk end user credential', e)
  }
}
export const deactivateZendeskSatisfactionRatingBackwardSync =
  (surveyId) => (dispatch, getState) => {
    const settings = R.mergeDeepRight(getSurveySettings(getState(), surveyId), {
      addSatisfaction: { isActive: false },
    })
    const survey = { settings }
    dispatch(updateSurveyFromId(surveyId, survey))
  }

export const searchFilterFeedback = (queryParams) => async (dispatch) => {
  try {
    const response = await api.getFeedbackSearchFilterResult(queryParams)
    const data = await response.json()
    dispatch(syncActions.setAnswers(data.count, data.results))
    return data
  } catch (e) {
    console.error('[ACTION](searchFilterFeedback) failed to run')
    throw e
  }
}

export const getUserProfile = () => async (dispatch) => {
  try {
    const response = await api.getUserProfile()
    const data = await response.json()
    const fullName = { full_name: `${data.first_name} ${data.last_name}` }
    dispatch(syncActions.setUserInfo(fullName))
    return R.mergeDeepRight(data, fullName)
  } catch (e) {
    console.error('Failed while getting user profile', e)
  }
}

export const updateUserProfile = (userProfile) => async (dispatch) => {
  try {
    const fullName = R.trim(userProfile.fullName)
    const splitName = {
      first_name: fullName.split(/\s+/)[0],
      last_name: fullName.split(/\s+/).slice(1).join(' '),
    }
    const response = await api.updateUserProfile(splitName)
    const data = await response.json()
    dispatch(syncActions.setUserInfo({ full_name: fullName }))
    return data
  } catch (e) {
    console.error('Failed while updating user profile', e)
  }
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/actions/async/index.js